// @givmo/payments — the SHARED payment Idempotency-Key holder (R-20 hoist of the ×3 app copies).
//
// ONE stateful holder implementing the at-most-once-per-attempt-series discipline shared by the
// donation + gift-card checkouts (donate), the Connect pay-sheet confirm (pay), and admin
// match-approval charges (admin). A charge attempt that fails on a RETRYABLE error (network, 5xx,
// 408/429) is retried with the SAME `Idempotency-Key` so the backend de-dupes a charge that
// actually went through but whose response we never saw. A NEW key per retry would defeat that
// de-dupe and risk a DOUBLE CHARGE.
//
// Contract (identical across every surface — this is the hoisted mechanism):
//   - LAZY: a key is minted on the FIRST charge attempt of a series, not before.
//   - REUSE: every retry of the SAME charge (same signature) reuses that key.
//   - CLEAR: on a CONFIRMED success, on an explicit reset, and when a NEW series begins (a different
//            signature), the key is dropped so the next attempt-series gets a fresh one.
//
// What is NOT shared — and MUST stay per-app — is the *charge signature* that defines when one
// attempt-series ends and the next begins. That predicate is genuinely different money semantics per
// surface (a donation's series-identity is target+amount+token; the pay sheet's is the instrument
// fingerprint alone; an admin approval's is the match-set+card). Collapsing them would be a money
// bug, so the holder is GENERIC over a signature type `S` and takes an injected `serialize(sig): string`
// that the consuming app supplies. The lifecycle logic below is identical for all of them.
//
// The key is a UUIDv4. It is isolated behind this small stateful holder so routes never juggle the
// lifecycle inline and the invariants are unit-testable in one place.

/** Generate a UUIDv4. `crypto.randomUUID` is available in every supported browser + Node 19+. */
export function uuidV4(): string {
  return crypto.randomUUID();
}

/**
 * The idempotency-key holder for one checkout / confirm / approval flow. Construct once per flow
 * mount (production apps hold it in a `useRef`); the route calls {@link keyFor} before each charge
 * attempt and {@link clear} on a confirmed success / reset.
 *
 * Generic over the app's charge signature `S`. The `serialize` function is the per-surface predicate
 * that decides series identity — a change in its output ends the current attempt-series and mints a
 * fresh key. `generate` is injectable for deterministic tests; production uses `crypto.randomUUID`.
 */
export class IdempotencyKeyHolder<S> {
  #key: string | null = null;
  #signature: string | null = null;
  readonly #serialize: (signature: S) => string;
  readonly #generate: () => string;

  constructor(serialize: (signature: S) => string, generate: () => string = uuidV4) {
    this.#serialize = serialize;
    this.#generate = generate;
  }

  /**
   * Return the key for this charge attempt. Mints lazily on the first attempt of a series and
   * REUSES it for every retry of the SAME charge (same serialized signature). A different signature
   * starts a fresh series with a new key. The same value is returned across retries of one series —
   * the guarantee a double-click / same-action retry relies on to avoid a second charge.
   */
  keyFor(signature: S): string {
    const sig = this.#serialize(signature);
    if (this.#key === null || this.#signature !== sig) {
      this.#key = this.#generate();
      this.#signature = sig;
    }
    return this.#key;
  }

  /** The current key without minting, or `null` when no series is active (test/inspection seam). */
  get current(): string | null {
    return this.#key;
  }

  /**
   * Drop the current key. Call on a CONFIRMED success (so a brand-new charge gets a brand-new key)
   * and on an explicit reset/abandon. The next {@link keyFor} mints fresh.
   */
  clear(): void {
    this.#key = null;
    this.#signature = null;
  }
}
